import React from 'react';
import { Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import useOrderDetails from '../hooks/useOrderDetails';
import {
  AddProductModal,
  ConfirmCaptureFundsModal,
  ConfirmPickupModal,
  ConfirmSendAndPayLaterModal,
  OrderActions,
  OrderDetailsTable,
  OrderHeader,
  OrderItemsTable,
} from './Order';
import './OrderDetails.css';

const OrderDetails = () => {
  const { id } = useParams();
  const orderId = !isNaN(+id) ? +id : 0;

  const {
    canCaptureFunds,
    canConfirmPickup,
    canConfirmSendAndPayLater,
    canEdit,
    closeAddProductModal,
    closeConfirmAndPayLaterModal,
    closeConfirmModal,
    closeConfirmPickupModal,
    getProductVisualTotal,
    handleAddProductToOrder,
    handleBackClick,
    handleCancelEdit,
    handleCaptureFunds,
    handleConfirmPickup,
    handleConfirmSendAndPayLater,
    handleDownload,
    handleEditToggle,
    handleProductSelect,
    handleQuantityChange,
    handleQuantityValue,
    handleRecalculateTotal,
    handleSaveChanges,
    handleSearchValue,
    handleSelectedWeightValue,
    hasCoupons,
    hasCouponsForActions,
    isPreparingOrder,
    modals,
    requiresSaveBeforeCharge,
    openAddProductModal,
    openConfirmAndPayLaterModal,
    openConfirmModal,
    openConfirmPickupModal,
    orderProductsAddedAfter,
    orderState,
    requiresRecalculation,
    sortedCartItems,
  } = useOrderDetails(orderId);

  const amountToCharge = hasCoupons
    ? orderState.recalculatedTotal
    : orderState.order
      ? parseFloat(orderState.order.total_inc_tax)
      : null;

  return (
    <div className="container mt-4">
      {(orderState.loading || orderState.updating) && (
        <div className="overlay">
          <Spinner animation="border" role="status">
            <span className="sr-only"></span>
          </Spinner>
        </div>
      )}

      <OrderHeader
        canEdit={canEdit()}
        customerName={`${orderState.order?.billing_address.first_name} ${orderState.order?.billing_address.last_name}`}
        onAddProductClick={openAddProductModal}
        onBackClick={handleBackClick}
        onDownload={handleDownload}
        orderId={orderState.order?.id}
        showRecalculateButton={hasCouponsForActions}
        recalculating={orderState.recalculating}
        onRecalculateClick={handleRecalculateTotal}
      />

      {hasCouponsForActions && requiresRecalculation && (
          <div
            className="alert alert-warning text-center"
            style={{ fontSize: 14, marginBottom: 16 }}
          >
            Esta orden tiene cupones aplicados. Da click en{' '}
            <strong>Recalcular precio final</strong> para obtener el total a
            cobrar.
          </div>
        )}

      {requiresSaveBeforeCharge && (
        <div
          className="alert alert-warning text-center"
          style={{ fontSize: 14, marginBottom: 16 }}
        >
          Hay cambios sin guardar en la orden. Guarda los cambios antes de
          cobrar.
        </div>
      )}

      <OrderItemsTable
        canEdit={canEdit()}
        cart={orderState.cart}
        editedItems={orderState.editedItems}
        editing={orderState.editing}
        handleCancelEdit={handleCancelEdit}
        handleEditToggle={handleEditToggle}
        handleSaveChanges={handleSaveChanges}
        onQuantityChange={handleQuantityChange}
        order={orderState.order}
        orderProductsAddedAfter={orderProductsAddedAfter}
        sortedCartItems={sortedCartItems}
      />

      <OrderDetailsTable
        cart={orderState.cart}
        order={orderState.order}
        paymentDetails={orderState.paymentDetails}
        orderProductsAddedAfter={orderProductsAddedAfter}
        shippingMethod={orderState.shippingMethod}
        coupons={orderState.coupons}
        recalculatedTotal={orderState.recalculatedTotal}
        recalcDiscountTotal={orderState.recalcDiscountTotal}
        recalcWarnings={orderState.recalcWarnings}
        isPreparingOrder={isPreparingOrder}
      />

      <OrderActions
        canCaptureFunds={canCaptureFunds}
        canConfirmPickup={canConfirmPickup}
        canConfirmSendAndPayLater={canConfirmSendAndPayLater}
        editing={orderState.editing}
        isProcessing={orderState.isProcessing}
        onCaptureFundsClick={openConfirmModal}
        onConfirmPickupClick={openConfirmPickupModal}
        onConfirmSendAndPayLaterClick={openConfirmAndPayLaterModal}
        recalculating={orderState.recalculating}
      />

      <AddProductModal
        getProductVisualTotal={getProductVisualTotal}
        isProcessing={orderState.isProcessing}
        onAddProduct={handleAddProductToOrder}
        onHide={closeAddProductModal}
        onProductSelect={handleProductSelect}
        onQuantityChange={handleQuantityValue}
        onSearchChange={handleSearchValue}
        onSelectedWeightChange={handleSelectedWeightValue}
        searchedProducts={orderState.searchedProducts}
        selectedProduct={orderState.selectedProduct}
        show={modals.addProduct}
      />

      <ConfirmCaptureFundsModal
        isProcessing={orderState.isProcessing}
        onConfirm={handleCaptureFunds}
        onHide={closeConfirmModal}
        show={modals.confirm}
        amountToCharge={amountToCharge}
        usedRecalc={hasCoupons && orderState.recalculatedTotal !== null}
      />

      <ConfirmPickupModal
        isProcessing={orderState.isProcessing}
        onConfirm={handleConfirmPickup}
        onHide={closeConfirmPickupModal}
        show={modals.confirmPickup}
      />

      <ConfirmSendAndPayLaterModal
        isProcessing={orderState.isProcessing}
        onConfirm={handleConfirmSendAndPayLater}
        onHide={closeConfirmAndPayLaterModal}
        show={modals.confirmAndPayLater}
      />

      {!!orderProductsAddedAfter?.length && (
        <div className="order-products-added-after">
          <div className='color'></div>
          <legend>Agregado por edición de orden</legend>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
